/**
 * Directives session helpers — distinguish the org-wide view from an open
 * patient chart.
 *
 * The `patient` object passed into these helpers is Vuex `login/getAllData`
 * → `phrData.patient`, built by pacmny-be from LA_user (see DataManagerImpl
 * getPatientByPatientId). It exposes:
 *   - id        → LA_user.id (often patients.mid in MySQL)
 *   - externId  → ZV_user_externid (patients.psersonalID / unique_ID)
 *   - externId2 → ZV_user_externid2 (accountnumber)
 *
 * Directives APIs use directive_record.patient_pid → MySQL patients.pid (auto-
 * increment PK). That is NOT the same as externId or patient.id. Resolve it via
 * resolveDirectivePatientPid() in directivesApi.js.
 */
import { sessionActorFrom } from '@/utils/sessionIdentity';
import { peekSessionSelfProfile } from '@/stores/sessionSelf';

/**
 * True when a staff member has entered a patient's chart, so `allData.patient`
 * is that patient rather than the logged-in user.
 *
 * This is NOT a scope signal. It says a chart is open, not who the person
 * behind the session is — read scope comes from privilege 65, see
 * utils/directivesScope.js.
 */
export function isViewingPatientChart(allData) {
  return !!allData?.viewingPatientChart;
}

/** patients.psersonalID from the session (externId). Not directive patient_pid. */
export function getSessionPatientPersonalId(patient = {}) {
  const id = patient.externId ?? patient.psersonalID;
  return id == null || id === '' ? '' : String(id);
}

/**
 * The acting user's keyon unique_ID, for directives API calls.
 *
 * Prefers MySQL sessionSelf; PHR fallback is manager when charting, else patient.
 */
export function getDirectiveActorId(allData = {}, sessionSelf) {
  return sessionActorFrom(allData, peekSessionSelfProfile(sessionSelf)).unique_ID;
}

/**
 * Org accountnumber for directives API calls.
 * While charting, the chart patient's org (clinical subject). Otherwise the
 * session actor's account (MySQL preferred).
 */
export function getDirectiveAccountNumber(allData = {}, sessionSelf) {
  const patient = allData.patient || {};
  if (isViewingPatientChart(allData)) {
    return patient.externId2 || patient.externid2 || '';
  }
  return sessionActorFrom(allData, peekSessionSelfProfile(sessionSelf)).accountnumber;
}
